//  (internal) - receive changes from frame on uiflowdesign
import logger from './logger';
import type { ExtensionMessage, FrameCommandDetail, DispatchData } from './Extension.types';
import { getComponentMode } from './sync';
import { AuthService } from '../services/auth.service';
import { toast } from 'react-toastify';
import PubSub from 'pubsub-js';
import { eventNames } from '../constants/eventNames';

// 🎯 CRITICAL: Track the currently active component for activity routing
// This ensures activities are only processed for the correct component
interface ActiveComponentData {
  componentId: string | null;
  componentUrl: string | null;
  componentName: string | null;
  timestamp: number;
}

let currentActiveComponent: ActiveComponentData | null = null;

//check if we are on any uiflowdesign page (should NEVER capture/record on our own pages)
function checkUiFlowTab(): boolean {
  const hostname = window.location.hostname;

  // If hostname is localhost or starts with our domain, we're on our own application
  const isOurDomain = (
    hostname === 'localhost' ||
    hostname.includes('localhost') ||
    hostname.includes('atpt.io') ||
    hostname.includes('127.0.0.1')
  );

  // Additional check: specific paths that are definitely our app pages
  const isKnownPath = (
    window.location.pathname.includes("/kanboard") ||
    window.location.pathname.includes("/controllers") ||
    window.location.pathname.includes("/help") ||
    window.location.pathname.includes("/components") ||
    window.location.pathname.includes("/work-connections") ||
    window.location.pathname.includes("/work-flows") ||
    window.location.pathname.includes("/scripts") ||
    window.location.pathname.includes("/licenses") ||
    window.location.pathname.includes("/projects") ||
    window.location.pathname.includes("/login") ||
    window.location.pathname === "/" ||
    window.location.pathname === "/index.html"
  );

  const isUiFlowTab = isOurDomain || isKnownPath;
  return isUiFlowTab;
}

/**
 * ⚠️ CRITICAL GUARD: Check if extension should be active
 *
 * Extension should ONLY work when:
 * 1. Recording is turned ON (isRecording flag in localStorage)
 * 2. User is logged IN (has valid auth token)
 *
 * This prevents the extension from:
 * - Processing messages when recording is turned OFF
 * - Processing messages when user is logged OUT
 * - Showing "new component detected" on other websites
 */
// Track if we've received a record-start message (more reliable than localStorage)
let recordStartReceived = false;

function shouldProcessExtensionMessages(): boolean {
  // Guard 1: Check if recording is enabled via extension toggle
  const storedValue = localStorage.getItem("default_ext_values");
  const isRecordingEnabled = storedValue ? JSON.parse(storedValue).isRecording === true : false;

  // ⚠️ CRITICAL: Also check if we received record-start message (handles race condition)
  // The content script sends record-start when recording is ON, this handles the case
  // where localStorage hasn't been synced yet
  const effectiveRecordingState = isRecordingEnabled || recordStartReceived;

  // Guard 2: Check if user is authenticated
  const authToken = AuthService.token;
  const isAuthenticated = !!authToken;

  const shouldProcess = effectiveRecordingState && isAuthenticated;

  // Reset warning flag when recording is enabled again
  if (shouldProcess && hasShownRecordingOffWarning) {
    hasShownRecordingOffWarning = false;
  }

  return shouldProcess;
}

let storedItems: ExtensionMessage[] = [];
let timer: NodeJS.Timeout | null = null;
let hasShownRecordingOffWarning = false; // Track if we've shown the warning to avoid spam

window.addEventListener(
  "message",
  function(e: MessageEvent<ExtensionMessage>) {
    const request = e.data;

    // 🗂️ DEBUG: Log page-elements-captured at entry point
    if (request?.data?.type === 'page-elements-captured') {
      console.log('[Extension.ts] 🗂️ window.message RECEIVED page-elements-captured:', {
        uiflowdesign: request.uiflowdesign,
        target: request.target,
        elementCount: request.data?.pageElements?.length,
        componentId: request.data?.componentId,
        hasData: !!request.data
      });
    }

    // 🗂️ DEBUG: Log capture status from background script
    if (request?.data?.type === 'capture-page-elements-status') {
      const status = request.data.status;
      const details = request.data.details;
      if (status === 'sent') {
        console.log('[Extension.ts] 🗂️ ✅ Background script:', details);
      } else if (status === 'no-tab') {
        console.warn('[Extension.ts] 🗂️ ❌ Background script:', details);
      } else {
        console.error('[Extension.ts] 🗂️ ❌ Background script:', details);
      }
    }

    // ⚠️ CRITICAL: Handle record-start and record-stop messages from extension
    // These messages tell us when recording state changes - we MUST update localStorage
    if (request.data?.type === 'record-start') {
      try {
        // Set the flag immediately - this handles race conditions
        recordStartReceived = true;
        hasShownRecordingOffWarning = false; // Reset warning flag

        const storedValue = localStorage.getItem("default_ext_values");
        const parsed = storedValue ? JSON.parse(storedValue) : {};
        parsed.isRecording = true;
        parsed.componentMode = 'dev';
        localStorage.setItem("default_ext_values", JSON.stringify(parsed));
      } catch (e) {
        logger.error('EXTENSION', 'Failed to update localStorage on record-start', e);
      }
      return; // Don't process further - this is a control message
    }

    if (request.data?.type === 'record-stop') {
      try {
        // Reset the flag
        recordStartReceived = false;

        const storedValue = localStorage.getItem("default_ext_values");
        const parsed = storedValue ? JSON.parse(storedValue) : {};
        parsed.isRecording = false;
        parsed.componentMode = 'user';
        localStorage.setItem("default_ext_values", JSON.stringify(parsed));
      } catch (e) {
        logger.error('EXTENSION', 'Failed to update localStorage on record-stop', e);
      }
      return; // Don't process further - this is a control message
    }

    // GUIDED-RECORD ROUTING (P7 bridge).
    // The route-mounted component-flow/IFrame.tsx and component-connection/IFrame.tsx
    // each translate AUTO_CREATE_NEW_COMPONENT → PubSub `createAutoComponent`, but
    // they're only mounted on the component/flow editor pages. During a Guided
    // record session the user is typically on CrawlerPage (no IFrame mounted),
    // so the dispatch would arrive nowhere. We publish the same PubSub event
    // here ONLY for flowId-bearing dispatches; the editor IFrames continue to
    // own the single-component case. This window listener is set up at module
    // load (api.service.ts imports `dispatch` from here on every page) so it's
    // always live.
    if (
      request?.data?.type === 'AUTO_CREATE_NEW_COMPONENT' &&
      request?.data?.element?.flowId
    ) {
      try {
        PubSub.publish(eventNames.createAutoComponent, {
          data: request.data.element,
          tabUrl: (request as any).tabUrl,
        });
      } catch (e) {
        logger.error('EXTENSION', 'Failed to publish createAutoComponent for guided record', e);
      }
      return; // Handled — global AppRouters subscriber takes it from here.
    }

    // ⚠️ CRITICAL GUARD: Block ALL extension messages if recording is OFF or user is logged OUT
    // EXCEPTION: Allow setURL through (extension sends setURL when capture button is clicked)
    // EXCEPTION: Allow screenshotCroppedManually through (for manual screenshot capture in edit modal)
    // EXCEPTION: Allow pick-element-detail responses through
    // EXCEPTION: Allow verifyAndSaveNewActivity through (for "New Activity" popup saves)
    const isSetURLMessage = request.data?.type === 'setURL';
    const isScreenshotCroppedManually = request.data?.type === 'screenshotCroppedManually';
    const isReturnElementDetail = request.data?.type === 'return-element-detail';
    const isVerifyAndSaveNewActivity = request.data?.type === 'verifyAndSaveNewActivity';
    // ⚠️ CRITICAL: Allow slideComplete through - it has its own isRecordEvent flag
    // The slideComplete event may arrive before record-start due to message ordering
    const isSlideComplete = request.data?.type === 'slideComplete';
    // Allow page-elements-captured through for Page Object Repository (Repo tab)
    const isPageElementsCaptured = request.data?.type === 'page-elements-captured';
    if (isPageElementsCaptured) {
      console.log('[Extension.ts] 🗂️ page-elements-captured message received at recording guard', {
        elementCount: request.data?.pageElements?.length,
        componentId: request.data?.componentId
      });
    }
    const isAllowedWithoutRecording = isSetURLMessage || isScreenshotCroppedManually || isReturnElementDetail || isVerifyAndSaveNewActivity || isSlideComplete || isPageElementsCaptured;

    // NOTE: Auto-enabling recording on capture has been DISABLED per user request
    // Recording will NOT automatically turn ON when capturing a page
    // User must manually enable recording if they want to record activities

    // Perform the blocking check - allow certain messages through even if recording is OFF
    // 🔧 2026-07-25 DEFENSIVE: bypass this guard when we have STRONG evidence recording
    // is genuinely on — record-start received from a legit extension source AND
    // localStorage.default_ext_values.isRecording === true. This handles the case
    // where AuthService.token is momentarily missing (auth refresh race, storage
    // sync lag, cross-storage propagation delay) but the user is definitely mid-
    // recording. The API call downstream will still fail if truly unauthenticated,
    // giving a proper 401 rather than a silent click drop.
    let bypassAuthForActiveRecording = false;
    try {
      if (request.data?.type === 'findByClickOfElement' || request.data?.type === 'selectDate' || request.data?.type === 'slideComplete') {
        const stored = localStorage.getItem("default_ext_values");
        const storedIsRecording = stored ? JSON.parse(stored).isRecording === true : false;
        const hasToken = !!AuthService.token;
        // Bypass fires ONLY when the auth check is the only failing gate — recording
        // is definitely on (both flags true) AND token is missing. If recording were
        // also off, we should NOT bypass (real off state, drop as intended).
        if (storedIsRecording && recordStartReceived && !hasToken) {
          bypassAuthForActiveRecording = true;
          console.warn('🔍 [CLICK FLOW] 2.95/4 🔧 auth-guard BYPASSED for active recording — AuthService.token was falsy but recording flags are both true', {
            activityType: request.data?.type,
            recognizer: (request.data?.element?.event?.path?.xPath || '').substring(0, 80),
          });
        }
      }
    } catch (_bypassEx) { /* ignore parse errors */ }

    if (!shouldProcessExtensionMessages() && !isAllowedWithoutRecording && !bypassAuthForActiveRecording) {
      // 🔍 [CLICK FLOW] 2.95/4 — the recording guard is dropping the message.
      // NOT throttled for findByClickOfElement so we always see it.
      if (request.data?.type === 'findByClickOfElement') {
        const stored = localStorage.getItem("default_ext_values");
        // Extra diagnostics: dump every localStorage key/length + sessionStorage
        // for the accessToken so we can see WHY AuthService.token is falsy while
        // the user is clearly on the app.
        let lsKeys: string[] = [];
        let hasAccessTokenKey = false;
        let accessTokenLen = 0;
        let hasRefreshTokenKey = false;
        let sessionTokenPresent: string | null = null;
        try {
          lsKeys = Object.keys(localStorage);
          const at = localStorage.getItem("accessToken");
          hasAccessTokenKey = at !== null;
          accessTokenLen = at ? at.length : 0;
          hasRefreshTokenKey = localStorage.getItem("refreshToken") !== null;
          sessionTokenPresent = sessionStorage.getItem("__atpt_token_present");
        } catch (_e) { /* storage may throw in some contexts */ }
        console.error('🔍 [CLICK FLOW] 2.95/4 ❌ DROPPED by recording guard', {
          storedIsRecording: stored ? JSON.parse(stored).isRecording : '(none)',
          recordStartReceived,
          hasAuthToken: !!AuthService.token,
          authTokenLen: (AuthService.token || '').length,
          localStorageHasAccessTokenKey: hasAccessTokenKey,
          localStorageAccessTokenLen: accessTokenLen,
          localStorageHasRefreshTokenKey: hasRefreshTokenKey,
          sessionStorageTokenPresent: sessionTokenPresent,
          localStorageKeyCount: lsKeys.length,
          localStorageKeys: lsKeys.filter(k => k.toLowerCase().includes('token') || k.toLowerCase().includes('auth') || k === 'default_ext_values' || k === 'user' || k === 'crossStorage'),
          recognizer: (request.data?.element?.event?.path?.xPath || '').substring(0, 100),
          ariaLabel: request.data?.element?.extras?.ariaLabel,
        });
      }
      // Throttle this warning to once per 30s to avoid console spam
      const now = Date.now();
      if (!((window as any).__extMsgIgnoredLastLog) || now - (window as any).__extMsgIgnoredLastLog > 30000) {
        (window as any).__extMsgIgnoredLastLog = now;
        logger.warn('EXTENSION', 'Message ignored - extension OFF or user logged out', {
          dataType: request.data?.type,
          pathname: window.location.pathname
        });
      }

      // Show user-friendly warning ONLY for capture/recording actions (not for passive events)
      const storedValue = localStorage.getItem("default_ext_values");
      const isRecordingEnabled = storedValue ? JSON.parse(storedValue).isRecording === true : false;
      // ⚠️ CRITICAL: Also consider recordStartReceived flag to avoid false warnings
      const effectiveRecording = isRecordingEnabled || recordStartReceived;

      // Only show warning for user-initiated capture actions (not for allowed actions like screenshot crop)
      const isCaptureAction = request.data?.type && [
        'findByClickOfElement'
      ].includes(request.data.type);

      if (!effectiveRecording && !hasShownRecordingOffWarning && isCaptureAction) {
        hasShownRecordingOffWarning = true;
        toast.warning(
          'Recording is OFF. Please turn ON the recorder toggle in the browser extension to capture pages and record activities.',
          {
            autoClose: 8000,
            position: 'top-center'
          }
        );
      }

      return; // Exit early - do NOT process any extension messages
    }

    // Checkpoint: Extension -> App
    // ⚠️ FIX: Include all activity types in batching to maintain proper activity ordering
    // Previously, selectDate and slideComplete were handled separately and dispatched immediately,
    // causing them to arrive at the backend before findByClickOfElement events
    // NOTE: doubleClick and rightClick are NOT batched - they clear the batch and dispatch immediately
    // because a double-click should cancel pending single-clicks
    const isBatchedActivityType = request.data && (
      request.data.type === "findByClickOfElement" ||
      request.data.type === "selectDate" ||
      request.data.type === "slideComplete"
    );

    if (isBatchedActivityType) {
      // 🔍 DIAGNOSTIC: log every batched activity ARRIVAL at Extension.ts (ATPT side).
      // This is the actual entry point from the extension's chrome.runtime → background →
      // ATPT tab. If we see 1/4 and 2/4 in the greyhound console but NOT this 3/4 here,
      // the background isn't forwarding to ATPT. If we see 3/4 but the activity never
      // appears, look at the skip/dispatch logs below.
      if (request.data.type === "findByClickOfElement") {
        console.info('🔍 [CLICK FLOW] 3/4 Extension.ts received findByClickOfElement', {
          recognizer: (request.data.element?.event?.path?.xPath || '').substring(0, 120),
          textXPath: (request.data.element?.event?.path?.textXPath || '').substring(0, 120),
          stableXPath: (request.data.element?.event?.path?.stableXPath || '').substring(0, 120),
          ariaLabel: request.data.element?.extras?.ariaLabel,
          dataTestId: request.data.element?.extras?.dataTestId,
          text: request.data.element?.extras?.text,
          componentId: request.data?.componentId,
          tabUrl: request.tabUrl,
          uiflowdesign: request.uiflowdesign,
          storedItemsCount: storedItems.length,
        });
      }
      try {
        // For findByClickOfElement, deduplicate by element type and xpath.
        //
        // 🐛 2026-08-08 (Fix 1): Two bugs in the previous dedup that this rewrite fixes:
        //
        //   BUG A — property name mismatch: the previous code read
        //   `event.path.xpath` (lowercase), but the actual field is `event.path.xPath`
        //   (capital X). Every pair compared as `undefined === undefined` → every
        //   subsequent findByClickOfElement in a batch was dropped as a "duplicate"
        //   regardless of whether it actually was one.
        //
        //   BUG B — no preference for ancestor-promoted recognizers: on sites like
        //   goindigo, one user click on the "Departure 9 Aug Sunday" summary button
        //   generates TWO extension dispatches: recording.js's raw mouseup captures
        //   the inner text `<span>` with recognizer `//span[normalize-space()='9 Aug']`,
        //   and click.js's ancestor-promotion re-captures the outer clickable div with
        //   recognizer `//div[@role='button' and @aria-label='departureDate 9 August 2026']`.
        //   The div-based recognizer is far more stable for playback. With Bug A masking
        //   the compare, whichever one arrived first "won" — usually the fragile span.
        //   With Bug A fixed we'd get BOTH activities recorded (still wrong — duplicates).
        //
        //   Fix: same-gesture detection via aria-label ⊃ text-substring heuristic.
        //   When a new aria-label recognizer arrives whose label CONTAINS the visible
        //   text of a previously-stored text-based recognizer (or vice versa), we treat
        //   them as the SAME user gesture and keep only the aria-label version.
        //
        // Preserves original exact-duplicate dedup for the true-duplicate case
        // (recording.js firing the same span twice from mousedown+mouseup paths).
        if (request.data.type === "findByClickOfElement" && storedItems.length > 0) {
          const newElementType = request.data.element?.type;
          const newRecognizer = request.data.element?.event?.path?.xPath || '';
          const newAriaMatch = newRecognizer.match(/@aria-label=['"]([^'"]+)['"]/);
          const newAriaText = newAriaMatch ? newAriaMatch[1] : '';
          const newTextMatch = newRecognizer.match(/normalize-space\(\)\s*=\s*['"]([^'"]+)['"]/);
          const newTextText = newTextMatch ? newTextMatch[1] : '';

          // Exact recognizer duplicate — drop new (existing behavior, now correctly compared).
          const exactDup = storedItems.some(
            item => item.data.type === "findByClickOfElement" &&
                    item.data.element?.type === newElementType &&
                    (item.data.element?.event?.path?.xPath || '') === newRecognizer
          );
          if (exactDup) {
            console.info('🔍 [CLICK FLOW] 3/4 ❌ Extension.ts deduplicated — dropped as exact-recognizer duplicate');
          } else if (newAriaText) {
            // NEW item has aria-label recognizer. Look for a stored text-based
            // recognizer whose visible text is a substring of the new aria-label —
            // that stored item is almost certainly the same click, captured earlier
            // via recording.js before click.js's ancestor promotion ran. REPLACE it
            // with the higher-quality aria-label version.
            const replaceIdx = storedItems.findIndex(item => {
              if (item.data.type !== "findByClickOfElement") return false;
              if (item.data.element?.type !== newElementType) return false;
              const storedRec = item.data.element?.event?.path?.xPath || '';
              const storedTextMatch = storedRec.match(/normalize-space\(\)\s*=\s*['"]([^'"]+)['"]/);
              const storedText = storedTextMatch ? storedTextMatch[1] : '';
              return !!storedText
                     && newAriaText.toLowerCase().indexOf(storedText.toLowerCase()) !== -1;
            });
            if (replaceIdx !== -1) {
              console.info(
                '🔍 [CLICK FLOW] 3/4 ↔ Extension.ts REPLACED text-based recognizer with ancestor-promoted aria-label version',
                { droppedRecognizer: storedItems[replaceIdx].data.element?.event?.path?.xPath?.substring(0, 100),
                  keptRecognizer: newRecognizer.substring(0, 100) }
              );
              storedItems[replaceIdx] = request;
            } else {
              storedItems.push(request);
            }
          } else if (newTextText) {
            // NEW item is text-based. If we already have an aria-label recognizer
            // stored whose label CONTAINS this text, drop the new one — the stored
            // aria-label version is the ancestor-promoted (better) version.
            const alreadyStoredBetter = storedItems.some(item => {
              if (item.data.type !== "findByClickOfElement") return false;
              if (item.data.element?.type !== newElementType) return false;
              const storedRec = item.data.element?.event?.path?.xPath || '';
              const storedAriaMatch = storedRec.match(/@aria-label=['"]([^'"]+)['"]/);
              const storedAriaText = storedAriaMatch ? storedAriaMatch[1] : '';
              return !!storedAriaText
                     && storedAriaText.toLowerCase().indexOf(newTextText.toLowerCase()) !== -1;
            });
            if (alreadyStoredBetter) {
              console.info(
                '🔍 [CLICK FLOW] 3/4 ❌ Extension.ts DROPPED text-based recognizer — ancestor-promoted aria-label version already stored'
              );
            } else {
              storedItems.push(request);
            }
          } else {
            // Neither aria-label nor text-based recognizer, or exact-dup path missed — accept.
            storedItems.push(request);
          }
        } else {
          storedItems.push(request);
        }
      } catch (e) {
        storedItems.push(request);
      }

      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(() => {
        if (storedItems.length > 0) {
          if (request.data?.type === "findByClickOfElement") {
            console.info('🔍 [CLICK FLOW] 3.5/4 debounce timer fired', {
              storedItemsCount: storedItems.length,
              typesInBatch: storedItems.map(i => i.data?.type),
              uiflowdesignFlags: storedItems.map(i => !!i.uiflowdesign),
            });
          }
          storedItems.forEach(item => {
            if (!item.uiflowdesign && item.data?.type === "findByClickOfElement") {
              console.warn('🔍 [CLICK FLOW] 4/4 ❌ DROPPED — item.uiflowdesign is falsy', {
                uiflowdesign: item.uiflowdesign,
                activityType: item.data?.type,
                tabUrl: item.tabUrl,
              });
            }
            if (item.uiflowdesign) {
              // ⚠️ CRITICAL FIX: Check if the SOURCE of the message is from a UIFlow tab
              // NOT if the CURRENT page is a UIFlow tab
              // We WANT to process messages from external tabs (test pages) when we're on the UI app
              const sourceUrl = item.tabUrl || '';
              const isSourceFromUiFlowTab = (
                sourceUrl.includes('localhost:3000') ||
                sourceUrl.includes('localhost:3001') ||
                sourceUrl.includes('localhost:3002') ||
                sourceUrl.includes('atpt.io')
              );

              if (!isSourceFromUiFlowTab) {
                // 🎯 CRITICAL: Validate componentId matches active component for ALL activity types
                // This prevents activities from being recorded on wrong components during
                // the transition window between URL change and new component creation
                const activityComponentId = item.data?.componentId;
                if (!validateActivityComponent(activityComponentId)) {
                  logger.info('EXTENSION', '🎯 Skipping activity - componentId mismatch', {
                    activityComponentId,
                    activityType: item.data?.type,
                    tabUrl: item.tabUrl
                  });
                  if (item.data?.type === "findByClickOfElement") {
                    console.warn('🔍 [CLICK FLOW] 4/4 ❌ DROPPED — componentId mismatch', {
                      activityComponentId,
                      activityType: item.data?.type,
                      tabUrl: item.tabUrl,
                    });
                  }
                  return; // Skip this activity
                }

                if (item.data?.type === "findByClickOfElement") {
                  console.info('🔍 [CLICK FLOW] 4/4 ✅ Extension.ts dispatching framecommand', {
                    activityType: item.data?.type,
                    componentId: item.data?.componentId,
                    tabUrl: item.tabUrl,
                    isFirefox: !!item.isFirefox,
                  });
                }
                document.dispatchEvent(
                  new CustomEvent<FrameCommandDetail>("framecommand", {
                    // ⚠️ FIX: Use item.data.frameid as fallback for tabUrl
                    // The background script sets frameid = sender.tab.url
                    detail: { ...item.data, isFirefox: item.isFirefox, tabUrl: item.tabUrl || item.data?.frameid }
                  })
                );
              } else if (item.data?.type === "findByClickOfElement") {
                console.warn('🔍 [CLICK FLOW] 4/4 ❌ DROPPED — message source IS the UIFlow tab itself', {
                  sourceUrl: sourceUrl.substring(0, 100),
                });
              }
            }
          });
          storedItems = [];
        }
      }, 500);
    } else if (request.data && (request.data.type === "doubleClick" || request.data.type === "rightClick")) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      storedItems = [];
      if (request.uiflowdesign) {
        // ⚠️ CRITICAL FIX: Check SOURCE URL, not current page
        const sourceUrl = request.tabUrl || '';
        const isSourceFromUiFlowTab = (
          sourceUrl.includes('localhost:3000') ||
          sourceUrl.includes('localhost:3001') ||
          sourceUrl.includes('localhost:3002') ||
          sourceUrl.includes('atpt.io')
        );
        if (!isSourceFromUiFlowTab) {
          const activityComponentId = request.data?.componentId;
          if (!validateActivityComponent(activityComponentId)) {
            logger.info('EXTENSION', '🎯 Skipping activity - componentId mismatch', {
              activityComponentId,
              activityType: request.data?.type,
              tabUrl: request.tabUrl
            });
            return;
          }
          document.dispatchEvent(
            new CustomEvent<FrameCommandDetail>("framecommand", {
              // ⚠️ FIX: Use request.data.frameid as fallback for tabUrl
              detail: { ...request.data, isFirefox: request.isFirefox, tabUrl: request.tabUrl || request.data?.frameid }
            })
          );
        }
      }
    } else if (request.data && request.data.type === "browserEvent") {
      setTimeout(() => {
        if (request.uiflowdesign) {
          // ⚠️ CRITICAL FIX: Check SOURCE URL, not current page
          const sourceUrl = request.tabUrl || '';
          const isSourceFromUiFlowTab = (
            sourceUrl.includes('localhost:3000') ||
            sourceUrl.includes('localhost:3001') ||
            sourceUrl.includes('localhost:3002') ||
            sourceUrl.includes('atpt.io')
          );
          if (!isSourceFromUiFlowTab) {
            const activityComponentId = request.data?.componentId;
            if (!validateActivityComponent(activityComponentId)) {
              logger.info('EXTENSION', '🎯 Skipping browserEvent - componentId mismatch', {
                activityComponentId,
                tabUrl: request.tabUrl
              });
              return;
            }
            document.dispatchEvent(
              new CustomEvent<FrameCommandDetail>("framecommand", {
                detail: {
                  ...request.data,
                  isFirefox: request.isFirefox,
                  // ⚠️ FIX: Use request.data.frameid as fallback for tabUrl
                  tabUrl: request.tabUrl || request.data?.frameid
                }
              })
            );
          }
        }
      }, 800);
    } else {
      if (request.uiflowdesign) {
        const messageType = request.data?.type;

        const isSetURLMessage = messageType === 'setURL';
        const isReturnElementDetail = messageType === 'return-element-detail';

        // ⚠️ CRITICAL FIX: Check SOURCE URL, not current page
        const sourceUrl = request.tabUrl || '';
        const isSourceFromUiFlowTab = (
          sourceUrl.includes('localhost:3000') ||
          sourceUrl.includes('localhost:3001') ||
          sourceUrl.includes('localhost:3002') ||
          sourceUrl.includes('atpt.io')
        );

        // ⚠️ CRITICAL: ALWAYS dispatch setURL messages (capture page)
        // ⚠️ CRITICAL: ALWAYS dispatch return-element-detail (element picker)
        // ⚠️ CRITICAL: ALWAYS dispatch verifyAndSaveNewActivity (New Activity popup saves)
        // NOTE: selectDate and slideComplete are now handled in the batching logic above to maintain proper activity ordering
        // Also dispatch messages from external tabs (test pages)
        const isVerifyAndSaveNewActivity = messageType === 'verifyAndSaveNewActivity';
        const shouldDispatch = !isSourceFromUiFlowTab || isSetURLMessage || isReturnElementDetail || isVerifyAndSaveNewActivity;

        if (messageType === 'page-elements-captured') {
          console.log('[Extension.ts] 🗂️ page-elements-captured in else block:', {
            shouldDispatch, isSourceFromUiFlowTab, sourceUrl,
            elementCount: request.data?.pageElements?.length,
            componentId: request.data?.componentId
          });
        }

        if (shouldDispatch) {
          if (messageType === 'page-elements-captured') {
            console.log('[Extension.ts] 🗂️ ✅ Dispatching framecommand for page-elements-captured');
          }
          document.dispatchEvent(
            new CustomEvent<FrameCommandDetail>("framecommand", {
              // ⚠️ FIX: Use request.data.frameid as fallback for tabUrl
              detail: { ...request.data, isFirefox: request.isFirefox, tabUrl: request.tabUrl || request.data?.frameid }
            })
          );
        }
      }
    }
  },
  false
);
// dispatches a command from uiflowdesign for frame

/*
    example:
    dispatch({type:'innerText', value:'something'})
*/
export function dispatch(data: DispatchData): void {
  try {
    // Detect Chrome/Chromium browser - required by background.js condition
    // The background script requires isChrome flag when messages originate from UIFlow tabs
    const hasChrome = !!(window as any).chrome;
    const isChromiumUserAgent = /Chrome|Chromium|Edg/i.test(navigator.userAgent) && !/Firefox/i.test(navigator.userAgent);
    const isChromeBrowser = hasChrome || isChromiumUserAgent;

    const event: ExtensionMessage = {
      uiflowdesign: true,
      target: "frame",
      data: data,
      isChrome: isChromeBrowser  // Required for background.js to accept messages from UIFlow tabs
    };
    const match = window.location.pathname.split("/");

    if (match.includes("rightBottomWindow")) {
      dispatchEvent(new CustomEvent("uiflowdesign", { detail: event }));
    } else {
      document.dispatchEvent(new CustomEvent("uiflowdesign", { detail: event }));
    }
  } catch (error) {
    logger.error('EXTENSION', 'Error dispatching message to extension', {
      error: error.message || error.toString(),
      dataType: data?.type
    });
    // Don't throw - just log the error to prevent app crashes
  }
}

// 🎯 CRITICAL: Get the currently active component for validation
export function getCurrentActiveComponent(): ActiveComponentData | null {
  return currentActiveComponent;
}

// 🎯 CRITICAL: Notify extension when component selection changes
// This ensures activities are routed to the correct component
function notifyExtensionOfComponentChange(componentData: any): void {
  try {
    if (componentData) {
      const activeComponentData: ActiveComponentData = {
        componentId: componentData.id || componentData.componentId || null,
        componentUrl: componentData.url || componentData.componentUrl || null,
        componentName: componentData.name || componentData.componentName || null,
        timestamp: Date.now()
      };

      currentActiveComponent = activeComponentData;

      // Dispatch to extension
      dispatch({
        type: 'set-active-component',
        componentId: activeComponentData.componentId,
        componentUrl: activeComponentData.componentUrl,
        componentName: activeComponentData.componentName
      });

      logger.debug('EXTENSION', '🎯 Active component changed, notified extension:', {
        componentId: activeComponentData.componentId,
        componentName: activeComponentData.componentName
      });
    } else {
      // Component was deselected/closed
      currentActiveComponent = null;

      dispatch({
        type: 'clear-active-component'
      });

      logger.debug('EXTENSION', '🎯 Active component cleared, notified extension');
    }
  } catch (error) {
    logger.error('EXTENSION', 'Error notifying extension of component change', {
      error: error.message || error.toString()
    });
  }
}

// 🎯 CRITICAL: Validate that activity belongs to the current component
// Returns true if activity should be processed, false if it should be ignored
export function validateActivityComponent(activityComponentId: string | undefined): boolean {
  // If no componentId in activity, allow it through.
  // Previously we rejected null-componentId activities within 5 seconds of a component switch,
  // but this was too aggressive and caused legitimate activities to be silently dropped.
  // The backend routes activities to the currently-viewed component regardless.
  if (!activityComponentId) {
    return true;
  }

  // If no active component in frontend, allow all activities
  if (!currentActiveComponent || !currentActiveComponent.componentId) {
    return true;
  }

  // Check if componentId matches
  const matches = activityComponentId === currentActiveComponent.componentId;

  if (!matches) {
    // ⚠️ FIX: Log mismatch but ALLOW the activity through.
    // Previously mismatched activities were silently dropped, which made recording
    // appear to stop. This happens when:
    //   1. ACTIVE_RECORDING_COMPONENT on target page has old component ID
    //   2. UIFlow tab navigated to a new component (auto-created)
    //   3. Activities arrive with old component ID → rejected → user sees no recording
    // The backend routes activities to the currently-viewed component regardless
    // of the componentId in the payload, so allowing them through is safe.
    logger.warn('EXTENSION', '🎯 Activity componentId mismatch (allowing through):', {
      activityComponentId,
      currentComponentId: currentActiveComponent.componentId,
      currentComponentName: currentActiveComponent.componentName
    });
    return true;  // Allow through — backend handles routing
  }

  return matches;
}

// 🎯 Subscribe to setActiveComponent events and notify extension
PubSub.subscribe(eventNames.setActiveComponent, (msg: string, data: any) => {
  notifyExtensionOfComponentChange(data);
});

// 🎯 Send all project component URLs to the extension so it can check
// whether a navigated URL already belongs to an existing component
// (prevents the "New web page detected" modal for already-captured pages)
export function setProjectComponentUrls(components: Array<{id: string, name: string, url: string}>): void {
  try {
    const componentUrls = components
      .filter(c => c.url)
      .map(c => ({
        componentId: c.id,
        componentUrl: c.url,
        componentName: c.name
      }));

    dispatch({
      type: 'set-project-components',
      components: componentUrls
    });

    logger.debug('EXTENSION', 'Sent project component URLs to extension:', { count: componentUrls.length });
  } catch (error: any) {
    logger.error('EXTENSION', 'Error sending project component URLs', {
      error: error.message || error.toString()
    });
  }
}
